import React, { useMemo, useCallback, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getSupabase } from "@/lib/hooks/api/utils";
import { useFTSSearchWithFullRows } from "@/lib/hooks/api/search/use-fts-search-with-full-rows";
import { useDebouncedSearch } from "@/lib/hooks/search/use-debounced-search";

export interface Company {
  id: string;
  name: string;
  website?: string;
  industry?: string;
  status?: string;
}

interface UseLinkCompanySearchOptions {
  contactId: string;
  currentCompanyId?: string;
}

export interface UseLinkCompanySearchReturn {
  searchValue: string;
  setSearchValue: (v: string) => void;
  clearSearch: () => void;
  hasInput: boolean;
  hasSearchTerm: boolean;
  isDebouncing: boolean;
  displayedCompanies: Company[];
  isListLoading: boolean;
  ftsError: unknown;
  selectedCompanyId: string | null;
  setSelectedCompanyId: (id: string | null) => void;
  showReplaceConfirm: boolean;
  setShowReplaceConfirm: (show: boolean) => void;
  handleSelectCompany: (company: Company) => void;
}

export function useLinkCompanySearch({
  contactId,
  currentCompanyId,
}: UseLinkCompanySearchOptions): UseLinkCompanySearchReturn {
  const { searchValue, debouncedSearchValue, isDebouncing, setSearchValue, clearSearch } =
    useDebouncedSearch({ initialValue: "", delay: 300 });
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(null);
  const [showReplaceConfirm, setShowReplaceConfirm] = useState(false);

  const hasInput = searchValue.trim().length > 0;
  const hasSearchTerm = debouncedSearchValue.trim().length > 1;

  const {
    search: ftsSearch,
    results: ftsResults,
    isLoading: isFtsLoading,
    error: ftsError,
  } = useFTSSearchWithFullRows({
    entityType: "companies",
    limit: 50,
    debounceMs: 300,
  });

  const { data: companiesData, isLoading: isLoadingCompanies } = useQuery({
    queryKey: ["companies-for-linking", contactId],
    queryFn: async () => {
      const supabase = getSupabase();
      const { data, error } = await supabase
        .from("companies")
        .select("id, name, website, industry, status")
        .order("name", { ascending: true })
        .limit(100);
      if (error) throw error;
      return data;
    },
    enabled: !!contactId,
  });

  React.useEffect(() => {
    if (hasSearchTerm) {
      ftsSearch(debouncedSearchValue);
    }
  }, [hasSearchTerm, debouncedSearchValue, ftsSearch]);

  const displayedCompanies = useMemo(() => {
    if (hasSearchTerm) return (ftsResults) ?? [];
    return companiesData ?? [];
  }, [hasSearchTerm, ftsResults, companiesData]);

  const isListLoading = hasSearchTerm ? isFtsLoading : isLoadingCompanies;

  const handleSelectCompany = useCallback(
    (company: Company) => {
      if (currentCompanyId && currentCompanyId !== company.id) {
        setSelectedCompanyId(company.id);
        setShowReplaceConfirm(true);
      } else {
        setSelectedCompanyId(company.id);
        setShowReplaceConfirm(false);
      }
    },
    [currentCompanyId]
  );

  return {
    searchValue,
    setSearchValue,
    clearSearch,
    hasInput,
    hasSearchTerm,
    isDebouncing,
    displayedCompanies,
    isListLoading,
    ftsError,
    selectedCompanyId,
    setSelectedCompanyId,
    showReplaceConfirm,
    setShowReplaceConfirm,
    handleSelectCompany,
  };
}
